import { Combobox, Input, Popover, Tooltip, UnstyledButton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { type Hue, hues } from '@tnn/core'
import { type ReactNode, useId } from 'react'
import type { IconComponent } from '../shell/Shell'
import { hueVars } from './display'
import classes from './pickers.module.css'

/**
 * Picking what a record wears as its own: an icon (`IconPicker`) and a colour (`ColorPicker`, one of the record colours
 * `Hue`). Each is a field like a select: the choice and its name, and the choices in a popover under it, round, each
 * named by its tooltip and to screen readers; picking one closes it. A kind of leave is the first to wear them (HR's
 * leave types).
 */

interface PickerFieldProps {
  label?: ReactNode
  description?: ReactNode
  error?: ReactNode
  withAsterisk?: boolean
  /** Shown, not changed: the field reads as a value and opens nothing */
  readOnly?: boolean
}

/** The field's face (what is picked, like an input) and the popover of choices it opens; `choices` gets `close` */
function PickerField({
  face,
  name,
  choices,
  readOnly,
  label,
  description,
  error,
  withAsterisk,
}: PickerFieldProps & { face: ReactNode; name: ReactNode; choices: (close: () => void) => ReactNode }) {
  const id = useId()
  const [opened, { toggle, close }] = useDisclosure(false)
  return (
    <Input.Wrapper id={id} labelElement="div" label={label} description={description} error={error} withAsterisk={withAsterisk}>
      <Popover
        opened={opened && !readOnly}
        onChange={(open) => !open && close()}
        position="bottom-start"
        trapFocus
        returnFocus
        middlewares={{ flip: true, shift: { padding: 16 } }}
      >
        <Popover.Target>
          <Input
            component="button"
            type="button"
            pointer={!readOnly}
            aria-labelledby={`${id}-label`}
            aria-haspopup="dialog"
            aria-expanded={opened}
            error={!!error}
            leftSection={face}
            rightSection={readOnly ? null : <Combobox.Chevron />}
            rightSectionPointerEvents="none"
            onClick={() => !readOnly && toggle()}
          >
            {name}
          </Input>
        </Popover.Target>
        <Popover.Dropdown>{choices(close)}</Popover.Dropdown>
      </Popover>
    </Input.Wrapper>
  )
}

export interface IconPickerOption {
  value: string
  /** Its name: the field's words once picked, the tooltip, and what a screen reader says */
  label: string
  icon: IconComponent
}

export interface IconPickerProps extends PickerFieldProps {
  options: IconPickerOption[]
  value: string | null
  onChange: (value: string) => void
  /** The colour the icons wear (the record's own): the picked one on its disc */
  hue?: Hue
}

/** One icon of a few, each in the record's colour: the picked one on its tonal disc, in the field and among the choices */
export function IconPicker({ options, value, onChange, hue, ...field }: IconPickerProps) {
  const picked = options.find((option) => option.value === value)
  const colour = hue ? hueVars(hue) : undefined
  return (
    <PickerField
      {...field}
      face={
        picked && (
          <span className={classes.faceIcon} style={colour}>
            <picked.icon size={16} />
          </span>
        )
      }
      name={picked?.label}
      choices={(close) => (
        <div
          className={classes.icons}
          role="radiogroup"
          aria-label={typeof field.label === 'string' ? field.label : undefined}
          style={colour}
        >
          {options.map((option) => (
            <Tooltip key={option.value} label={option.label}>
              <UnstyledButton
                className={classes.icon}
                role="radio"
                aria-checked={option.value === value}
                aria-label={option.label}
                data-picked={option.value === value || undefined}
                onClick={() => {
                  onChange(option.value)
                  close()
                }}
              >
                <option.icon size={20} />
              </UnstyledButton>
            </Tooltip>
          ))}
        </div>
      )}
    />
  )
}

export interface ColorPickerProps extends PickerFieldProps {
  value: Hue | null
  onChange: (hue: Hue) => void
  /** A colour's name: the field's words once picked, the tooltip, and what a screen reader says */
  labelOf: (hue: Hue) => string
  /** The colours offered (default: every record colour, in the palette's order) */
  options?: readonly Hue[]
}

/** One of the record colours, as swatches of their dot tone: the picked one in the field, and ringed and ticked among them */
export function ColorPicker({ value, onChange, labelOf, options = hues, ...field }: ColorPickerProps) {
  return (
    <PickerField
      {...field}
      face={value && <span className={classes.faceSwatch} style={hueVars(value)} />}
      name={value ? labelOf(value) : null}
      choices={(close) => (
        <div className={classes.swatches} role="radiogroup" aria-label={typeof field.label === 'string' ? field.label : undefined}>
          {options.map((hue) => (
            <Tooltip key={hue} label={labelOf(hue)}>
              <UnstyledButton
                className={classes.swatch}
                role="radio"
                aria-checked={hue === value}
                aria-label={labelOf(hue)}
                data-picked={hue === value || undefined}
                style={hueVars(hue)}
                onClick={() => {
                  onChange(hue)
                  close()
                }}
              >
                {hue === value && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <path d="m5 12 5 5 9-10" />
                  </svg>
                )}
              </UnstyledButton>
            </Tooltip>
          ))}
        </div>
      )}
    />
  )
}
